import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './ui/table';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ShieldCheck, ChevronLeft, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const getAuthToken = () => localStorage.getItem('auth_token');

const formatBytes = (bytes) => {
  if (!bytes) return '0 KB';
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(2)} MB`;
};

export const ExportAuditLog = () => {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const pageSize = 50;

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/api/admin/export-audit-log?page=${page}&page_size=${pageSize}`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    })
      .then((r) => r.json())
      .then((d) => {
        setItems(d.items || []);
        setTotal(d.total || 0);
      })
      .finally(() => setLoading(false));
  }, [page]);

  return (
    <Card data-testid="export-audit-log-view">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-emerald-600" />
          Registro de descargas de datos personales
        </CardTitle>
        <p className="text-sm text-gray-500">
          Toda descarga de datos personales queda acá: quién, qué, cuántas filas, cuánto pesa y desde qué IP. Solo superadmin la ve.
        </p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table data-testid="export-audit-log-table">
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Usuario</TableHead>
                <TableHead>Qué exportó</TableHead>
                <TableHead>Filtros</TableHead>
                <TableHead>Filas</TableHead>
                <TableHead>Peso</TableHead>
                <TableHead>IP</TableHead>
                <TableHead>Disclaimer</TableHead>
                <TableHead>Cifrado</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} className="text-center text-gray-400">Cargando...</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center text-gray-400">Todavía no hay descargas registradas</TableCell></TableRow>
              ) : (
                items.map((item, idx) => (
                  <TableRow key={idx} data-testid={`export-audit-log-row-${idx}`}>
                    <TableCell className="whitespace-nowrap text-xs">{new Date(item.timestamp).toLocaleString('es-AR')}</TableCell>
                    <TableCell>
                      <div className="text-sm">{item.user_email}</div>
                      <div className="text-xs text-gray-400">{item.role}</div>
                    </TableCell>
                    <TableCell><Badge variant="outline">{item.export_type}</Badge></TableCell>
                    <TableCell className="max-w-[220px] truncate text-xs text-gray-500" title={JSON.stringify(item.filters)}>
                      {Object.entries(item.filters || {}).filter(([, v]) => v).map(([k, v]) => `${k}=${v}`).join(', ') || '-'}
                    </TableCell>
                    <TableCell>{item.row_count}</TableCell>
                    <TableCell>{formatBytes(item.file_size_bytes)}</TableCell>
                    <TableCell className="text-xs">{item.ip_address}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{item.disclaimer_version}</Badge>
                    </TableCell>
                    <TableCell>
                      {item.encrypted ? (
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">{item.encryption_format}</Badge>
                      ) : '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-gray-500">{total} descargas registradas</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="export-audit-log-prev-btn">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page * pageSize >= total} onClick={() => setPage((p) => p + 1)} data-testid="export-audit-log-next-btn">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ExportAuditLog;
